import { useId, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { FocalPoint } from '@/lib/api/types';
import { Button } from '@/lib/ui/Button';
import { cn } from '@/lib/ui/cn';

/** Arrow keys move the point by this fraction of the photo (D-153 § focal: 5%). */
export const FOCAL_STEP = 0.05;

const clamp = (v: number) => Math.min(1, Math.max(0, v));
/** Four decimals, as the server stores it. */
const round = (v: number) => Math.round(v * 10000) / 10000;

/** The focal point a click at (clientX, clientY) on `rect` names. */
export function focalFromClick(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): FocalPoint {
  return {
    x: round(clamp((clientX - rect.left) / rect.width)),
    y: round(clamp((clientY - rect.top) / rect.height)),
  };
}

/**
 * The point after an arrow key. In a right-to-left console ArrowLeft still moves
 * the point LEFT on the photo: the photograph is not mirrored, so the key follows
 * the picture rather than the reading direction.
 */
export function nudgeFocal(value: FocalPoint | null, key: string): FocalPoint | null {
  const p = value ?? { x: 0.5, y: 0.5 };
  switch (key) {
    case 'ArrowLeft':
      return { x: round(clamp(p.x - FOCAL_STEP)), y: p.y };
    case 'ArrowRight':
      return { x: round(clamp(p.x + FOCAL_STEP)), y: p.y };
    case 'ArrowUp':
      return { x: p.x, y: round(clamp(p.y - FOCAL_STEP)) };
    case 'ArrowDown':
      return { x: p.x, y: round(clamp(p.y + FOCAL_STEP)) };
    default:
      return null;
  }
}

/** CSS `object-position` for a focal point — the same rule fe-svet's `focalPosition()` applies. */
export function focalPosition(value: FocalPoint | null | undefined): string {
  if (!value) return '50% 50%';
  const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
  return `${pct(value.x)} ${pct(value.y)}`;
}

/**
 * Set a photo's focal point (round 8, R8-05): the spot every crop of it keeps in
 * frame. Click the photo, or focus it and nudge with the arrow keys; "Reset to
 * centre" clears it. Two sample crops — a wide banner and a square card — show
 * what the point does before anything is saved.
 */
export function FocalPointPicker({
  src,
  alt,
  value,
  onChange,
  disabled,
}: {
  src: string;
  alt: string;
  value: FocalPoint | null;
  onChange: (next: FocalPoint | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement>(null);
  const id = useId();
  const point = value ?? { x: 0.5, y: 0.5 };
  const pct = (v: number) => Math.round(v * 100);
  const where = value
    ? t('media.focal.position', { x: pct(value.x), y: pct(value.y) })
    : t('media.focal.centre');

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled || !imgRef.current) return;
    onChange(focalFromClick(imgRef.current.getBoundingClientRect(), e.clientX, e.clientY));
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const next = nudgeFocal(value, e.key);
    if (!next) return;
    e.preventDefault();
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-baseline justify-between gap-sm">
        <span className="eyebrow text-ink-mute" id={`${id}-label`}>
          {t('media.focal.label')}
        </span>
        <span className="font-mono text-label-sm text-ink-mid" aria-live="polite">
          {where}
        </span>
      </div>
      <p className="max-w-prose text-small text-ink-mute" id={`${id}-help`}>
        {t('media.focal.help')}
      </p>
      <div className="flex flex-wrap items-start gap-md">
        {/* The photo is the control. It is a focusable group rather than a
            button so a click can report WHERE it landed; the arrow keys do the
            same for a keyboard, and the live text above says where it is. */}
        <div
          role="group"
          tabIndex={disabled ? -1 : 0}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-help`}
          aria-disabled={disabled || undefined}
          data-testid="focal-surface"
          onClick={onClick}
          onKeyDown={onKeyDown}
          className={cn(
            'relative inline-block max-w-full select-none overflow-hidden rounded-sm bg-canvas-deep-sand',
            'focus-visible:shadow-focus focus:outline-hidden',
            disabled ? 'cursor-not-allowed opacity-70' : 'cursor-crosshair',
          )}
          // The photo is laid out left-to-right in either chrome direction: the
          // point is a position on the picture, not on the page.
          dir="ltr"
        >
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            draggable={false}
            className="block max-h-[320px] w-auto max-w-full"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-canvas-paper shadow-[0_0_0_1.5px_rgba(0,0,0,0.55)]"
            style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }}
          >
            <span className="absolute left-1/2 top-1/2 h-[4px] w-[4px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-canvas-paper" />
          </span>
        </div>
        <div className="flex flex-col gap-xs" aria-hidden>
          <span className="font-mono text-label-sm uppercase tracking-[0.08em] text-ink-mute">
            {t('media.focal.previews')}
          </span>
          <img
            src={src}
            alt=""
            className="h-[64px] w-[160px] rounded-sm object-cover"
            style={{ objectPosition: focalPosition(value) }}
          />
          <img
            src={src}
            alt=""
            className="h-[96px] w-[96px] rounded-sm object-cover"
            style={{ objectPosition: focalPosition(value) }}
          />
        </div>
      </div>
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || !value}
          onClick={() => onChange(null)}
        >
          {t('media.focal.reset')}
        </Button>
      </div>
    </div>
  );
}
